import { createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles'

import {
  black,
  white,
  normalGray,
  darkGray,
  orange,
  orangeSelected,
  backgroundColor,
  gray,
  lightGray,
  lightOrange,
  lightOrangeSolid,
} from './constants/colorConstants'

const primaryFontFamily = [
  'Roboto',
  'Franklin Gothic Medium',
  'Tahoma',
  'sans-serif',
].join(',')

const secondaryFontFamily = ['Lato', 'Trebuchet MS', 'sans-serif'].join(',')

const common = {
  typography: {
    useNextVariants: true,
    fontFamily: primaryFontFamily,
    primary: {
      fontFamily: primaryFontFamily,
    },
    secondary: {
      fontFamily: secondaryFontFamily,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1440,
      xl: 1920,
    },
  },
  mundi: {
    font: {
      roboto400Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 400,
        fontDisplay: 'swap',
      },
      roboto500Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 500,
        fontDisplay: 'swap',
      },
      roboto600Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 600,
        fontDisplay: 'swap',
      },
      roboto700Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 700,
        fontDisplay: 'swap',
      },
      roboto800Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 800,
        fontDisplay: 'swap',
      },
      roboto900Normal: {
        fontFamily: primaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 900,
        fontDisplay: 'swap',
      },
      lato400Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 400,
        fontDisplay: 'swap',
      },
      lato500Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 500,
        fontDisplay: 'swap',
      },
      lato600Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 600,
        fontDisplay: 'swap',
      },
      lato700Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 700,
        fontDisplay: 'swap',
      },
      lato800Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 800,
        fontDisplay: 'swap',
      },
      lato900Normal: {
        fontFamily: secondaryFontFamily,
        fontStyle: 'normal',
        fontWeight: 900,
        fontDisplay: 'swap',
      },
    },
  },
}

const loginLightTheme = responsiveFontSizes(
  createMuiTheme({
    palette: {
      type: 'light',
      common: {
        black: '#000',
        white: '#FFF',
      },
      primary: {
        main: '#F6F6F6',
        contrastText: '#404042',
      },
      secondary: {
        main: '#F05A26',
        contrastText: '#ffffff',
      },
      background: {
        default: '#FFFFFF',
      },
    },
    ...common,
  })
)

const lightTheme = responsiveFontSizes(
  createMuiTheme({
    palette: {
      type: 'light',
      common: {
        black: black,
        white: white,
      },
      primary: {
        main: black,
        contrastText: darkGray,
      },
      secondary: {
        main: white,
        contrastText: gray,
      },
      background: {
        default: backgroundColor,
      },
    },
    overrides: {
      RaMultiLevelMenu: {
        root: {
          marginTop: '55px',
          backgroundColor: 'white',
        },
      },
      RaMenuItemCategory: {
        icon: {
          marginRight: '17px',
        },
        link: {
          flexDirection: 'row',
          whiteSpace: 'normal',
          '& span': {},
        },
        root: {
          fontSize: '1rem',
          '&:hover': {
            backgroundColor: white,
          },
          '& span': {
            ...common.mundi.font.roboto500Normal,
            fontSize: '1rem!important',
            color: black,
          },
        },
        active: {
          backgroundColor: orangeSelected,
          '&:hover': {
            backgroundColor: orangeSelected,
          },
          borderRadius: '8px',
          marginLeft: '7px',
          marginRight: '0px',
          '& span': {
            ...common.mundi.font.roboto600Normal,
            fontSize: '1rem!important',
            color: orange,
          },
          '& a': {
            marginLeft: '-7px',
          },
        },
      },
      RaEnterpriseAppBar: {
        title: {
          opacity: 0,
        },
      },
      MuiAppBar: {
        root: {
          paddingTop: '26px',
          paddingBottom: '26px',
          boxShadow:
            '0px 2px 1px -1px #e5e5e5, 0px 1px 1px 0px #e5e5e5, 0px 1px 3px 0px #e5e5e5',
          '& .MuiButtonBase-root:nth-child(3)': {
            display: 'none',
          },
        },
      },
      RaSidebar: {
        drawerPaper: {
          backgroundColor: 'white',
          height: '100%',
          boxShadow:
            '2px 0px 1px -1px rgba(0,0,0,0.2), 1px 0px 3px 0px rgba(0,0,0,0.1)',
          position: 'sticky',
          paddingRight: '8px',
        },
      },
      MuiTabs: {
        flexContainer: {
          justifyContent: 'space-evenly',
        },
      },
      MuiTab: {
        textColorInherit: {
          color: lightGray,
          fontSize: '14px',
          lineHeight: '16.8px',
          ...common.mundi.font.lato500Normal,
          '&.Mui-selected': {
            color: orange,
            borderBottom: `solid 1px ${orange}`,
          },
        },
      },
      MuiTableSortLabel: {
        root: {
          color: normalGray,
          fontSize: '12px',
          lineHeight: '14.4px',
          ...common.mundi.font.lato400Normal,
        },
      },
      MuiTableCell: {
        root: {
          fontSize: '13px',
          lineHeight: '15.23px',
          ...common.mundi.font.roboto400Normal,
          '& .MuiTableCell-root .MuiTableSortLabel-root': {
            color: black,
            ...common.mundi.font.roboto500Normal,
          },
          '& .MuiTableCell-root': {
            paddingTop: '10px',
            paddingBottom: '10px',
            ...common.mundi.font.roboto400Normal,
          },
          '& .MuiTable-root': {
            boxShadow:
              '0px 2px 1px -1px #e5e5e5, 0px 1px 1px 0px #e5e5e5, 0px 1px 3px 0px #e5e5e5',
          },
        },
        sizeSmall: {
          padding: '6px',
        },
      },
      RaList: {
        main: {
          margin: '0 !important',
        },
      },
      MuiInput: {
        colorSecondary: {
          '&.MuiInput-underline:after': {
            borderBottomColor: black,
          },
        },
        underline: {
          '&:after': {
            transition: 'none',
          },
        },
      },
      MuiDialog: {
        paper: {
          '& .MuiCardContent-root': {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          },
          '& .MuiIconButton-root': {
            display: 'none',
          },
          '&.MuiDialog-paperWidthSm': {
            maxWidth: '700px',
            overflow: 'hidden',
          },
        },
      },
      MuiFilledInput: {
        input: {
          backgroundColor: lightOrangeSolid,
          borderTopRightRadius: '10px',
          borderTopLeftRadius: '10px',
          borderBottomRightRadius: '0px',
          borderBottomLeftRadius: '0px',
        },
        adornedStart: {
          backgroundColor: lightOrangeSolid,
          borderTopRightRadius: '10px',
          borderTopLeftRadius: '10px',
          borderBottomRightRadius: '0px',
          borderBottomLeftRadius: '0px',
          '& .MuiFilledInput-input': {
            backgroundColor: 'transparent',
          },
        },
        underline: {
          '&:hover:before': {
            borderBottom: `1px solid ${orange}`,
          },
          '&:before': {
            borderBottom: `1px solid ${orange}`,
          },
          '&:hover:after': {
            borderBottom: `2px solid ${orange}`,
          },
          '&:after': {
            borderBottom: `2px solid ${orange}`,
          },
        },
        root: {
          backgroundColor: lightOrangeSolid,
          color: gray,
          '&:hover': {
            backgroundColor: `${lightOrangeSolid}!important`,
            color: gray,
          },
          '& input[type="date"]': {
            overflow: 'hidden',
          },
          '& input[type="date"]::-webkit-datetime-edit-year-field': {
            position: 'absolute !important',
            left: '130px',
          },
          '& input[type="date"]::-webkit-datetime-edit-text': {
            marginRight: '30px',
            marginLeft: '30px',
          },
          '& input[type="date"]::-webkit-datetime-edit-month-field': {
            position: 'absolute !important',
            left: '64px',
          },
          '& input[type="date"]::-webkit-datetime-edit-day-field': {
            position: 'absolute !important',
            left: '10px',
          },
          '& input[type="date"]::-webkit-calendar-picker-indicator': {
            background: 'url(/icons/calendar.png)',
            backgroundPosition: '270px 10px',
            backgroundRepeat: 'no-repeat',
            paddingTop: '14px',
            paddingBottom: '15px',
            paddingLeft: '270px',
            paddingRight: '10px',
            marginTop: '-30px',
            marginBottom: '-15px',
            marginLeft: '-270px',
            marginRight: '-10px',
            cursor: 'pointer',
            zIndex: '10',
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          fontSize: '13px',
          lineHeight: '15.23px',
        },
      },
      RaAutocompleteSuggestionList: {
        suggestionsContainer: {
          zIndex: 1500,
        },
      },
      RaAutocompleteInput: {
        container: {
          display: 'inline',
        },
      },
      MuiStepper: {
        horizontal: {
          display: 'none',
        },
      },
      MuiChip: {
        outlinedSecondary: {
          marginTop: 8,
          maxWidth: 165,
          color: `${orange}!important`,
          border: `solid 1px ${orange}!important`,
        },
        deleteIconOutlinedColorSecondary: {
          color: orange,
          '&:hover': {
            color: orangeSelected,
          },
        },
      },
      MuiDropzoneArea: {
        textContainer: {
          '& *': {
            color: `${orange}!important`,
          },
        },
      },
      PrivateTabIndicator: {
        colorPrimary: {
          backgroundColor: orange,
        },
      },
      RaLayout: {
        root: {
          backgroundColor: 'none',
          display: 'block',
          minWidth: 'unset',
        },
        appFrame: {
          margin: '0 !important',
          flexDirection: 'row',
        },
        content: {
          padding: '0 !important',
        },
      },
      RaSkipToContentButton: {
        skipToContentButton: {
          display: 'none !important',
        },
      },
      MuiDialogActions: {
        root: {
          display: 'flex!important',
          flexDirection: 'row!important',
          justifyContent: 'flex-end!important',
          alignItems: 'center!important',
          '& .MuiButton-root:first-of-type': {
            backgroundColor: white,
            border: `solid 1px ${orange}`,
            padding: '10px 20px',
            borderRadius: '28px',
            ...common.mundi.font.roboto400Normal,
            color: orange,
            fontSize: '13px',
            lineHeight: '15.23px',
            marginRight: '5px',
            textTransform: 'uppercase',
            '&:hover': {
              backgroundColor: lightOrange,
            },
          },
          '& .MuiButton-root': {
            backgroundColor: orange,
            border: `solid 1px ${orange}`,
            padding: '10px 20px',
            borderRadius: '28px',
            ...common.mundi.font.roboto400Normal,
            color: white,
            fontSize: '13px',
            lineHeight: '15.23px',
            marginBottom: '30px',
            marginRight: '5px',
            textTransform: 'uppercase',
            '&:hover': {
              backgroundColor: orange,
            },
          },
          '& .MuiButton-contained.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            boxShadow: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiDrawer: {
        root: {
          display: 'none',
        },
      },
    },
    ...common,
  })
)

export { lightTheme, loginLightTheme }
export default lightTheme
